import CashPreAccountingApi from "./CashPreAccountingApi";
import { CashPreAccReAccountResult } from "../models/CashPreAccounting";
import {
  CashPreAccAccNo,
  CashPreAccDimCode,
  CashPreAccDocItem,
  CashPreAccEditDimension,
  CashPreAccEditDimensionDefaultProps,
  CashPreAccEditItem,
  CashPreAccEditItemDefaultProps,
  CashPreAccEditorData,
  CashPreAccEditorFlags,
  CashPreAccEditorHead,
  CashPreAccEditorHeadDefaultProps,
  CashPreAccSaveRequest,
  CashPreAccSaveResult,
} from "../models/CashPreAccEditing";

// A pénztári kontír-szerkesztő singleton store-ja — a banki
// BankPreAccEditorStore pénztár-oldali párja: tétel-felvétel és -törlés
// K2D-hűen nincs, ezért csak modified-követés van a tételekre;
// a dimenzió-bontás viszont teljes (új/módosított/törölt).
export class CashPreAccEditorStore {
  statementId = 0;

  head: CashPreAccEditorHead = { ...CashPreAccEditorHeadDefaultProps };
  originalAccStatus = 1;
  editable = false;
  reAccountable = false;
  uploadable = false;

  items: CashPreAccEditItem[] = [];
  originalItems: CashPreAccEditItem[] = [];
  modifiedItems: CashPreAccEditItem[] = [];

  docItems: CashPreAccDocItem[] = [];

  isItemsLoading = false;
  isSaving = false;
  // Az újrakontírozás saját jelzője: ne a Mentés gomb pörögjön, amikor az Újrakontírozásra kattintottak
  isReAccounting = false;
  isReadOnlyView = false;

  selectedItem: CashPreAccEditItem = { ...CashPreAccEditItemDefaultProps };
  positiveSelectedItem: CashPreAccEditItem = { ...CashPreAccEditItemDefaultProps };
  negativeSelectedItem: CashPreAccEditItem = { ...CashPreAccEditItemDefaultProps };

  dimensionsRows: CashPreAccEditDimension[] = [];
  originalDimensionsRows: CashPreAccEditDimension[] = [];
  newDimensions: CashPreAccEditDimension[] = [];
  modifiedDimensions: CashPreAccEditDimension[] = [];
  deletedDimensions: number[] = [];

  selectedDimension: CashPreAccEditDimension = { ...CashPreAccEditDimensionDefaultProps };
  positiveSelectedDimension: CashPreAccEditDimension = { ...CashPreAccEditDimensionDefaultProps };
  negativeSelectedDimension: CashPreAccEditDimension = { ...CashPreAccEditDimensionDefaultProps };
  positiveSelectedDimCodeType = 5;
  negativeSelectedDimCodeType = 5;

  accNos: CashPreAccAccNo[] = [];
  dimCodes: CashPreAccDimCode[] = [];
  detailedTypes: { id: number; name: string }[] = [];
  flags: CashPreAccEditorFlags = { extBook: false, dim49: false, checkDetType: false, upChangeable: false, dimTypeLive: {}, dimTypeRequired: {} };

  serCars: any[] = [];
  projects: any[] = [];
  pos: any[] = [];
  users: any[] = [];
  stations: any[] = [];
  tools: any[] = [];
  private dimLookupsLoaded = false;

  // Az új bontás-sorok negatív ideiglenes azonosítója
  private tempId = 0;

  nextTempId(): number {
    this.tempId--;
    return this.tempId;
  }

  get hasChanges(): boolean {
    return (
      this.modifiedItems.length > 0 ||
      this.newDimensions.length > 0 ||
      this.modifiedDimensions.length > 0 ||
      this.deletedDimensions.length > 0
    );
  }

  resetDeltas() {
    this.modifiedItems = [];
    this.newDimensions = [];
    this.modifiedDimensions = [];
    this.deletedDimensions = [];
    this.tempId = 0;
  }

  // Egy tétel „módosított"-státuszának karbantartása a 6 írható mező alapján
  syncModified(item: CashPreAccEditItem) {
    const original = this.originalItems.find((x) => x.id === item.id);
    if (!original) return;

    const fields: (keyof CashPreAccEditItem)[] = [
      "accCode", "detailedType", "dimCode1", "dimCode2", "dimCode3", "note",
    ];
    const changed = fields.some((key) => item[key] != original[key]);

    if (changed) {
      if (!this.modifiedItems.some((x) => x.id === item.id)) {
        this.modifiedItems.push(item);
      }
    } else {
      this.modifiedItems = this.modifiedItems.filter((x) => x.id !== item.id);
    }
  }

  // Egy bontás-sor „módosított"-státuszának karbantartása a MENTETT mezők alapján.
  // A döntésnek EGY helyen és a mezők teljes halmazán kell megtörténnie — a banki
  // store indoklása szerint (két hívó komponens egymás jelzését törölné).
  syncDimensionModified(dim: CashPreAccEditDimension) {
    const original = this.originalDimensionsRows.find((x) => x.id === dim.id);
    if (!original) return;

    const fields: (keyof CashPreAccEditDimension)[] = [
      "dimCode", "total",
      "extProdParts", "extProdPartner", "productParts", "production", "stock",
      "carId", "userId", "stationId", "toolId", "project", "poId",
    ];
    const changed = fields.some((key) => dim[key] != original[key]);

    if (changed) {
      if (!this.modifiedDimensions.some((x) => x.id === dim.id)) {
        this.modifiedDimensions.push(dim);
      }
    } else {
      this.modifiedDimensions = this.modifiedDimensions.filter((x) => x.id !== dim.id);
    }
  }

  // A kivonat-specifikus állapot ürítése. A lookupok szándékosan maradnak:
  // nem kivonathoz kötöttek, a következő sikeres betöltés felülírja őket.
  resetEditorState() {
    this.head = { ...CashPreAccEditorHeadDefaultProps };
    this.originalAccStatus = 1;
    this.editable = false;
    this.reAccountable = false;
    this.uploadable = false;

    this.items = [];
    this.originalItems = [];
    this.docItems = [];
    this.dimensionsRows = [];
    this.originalDimensionsRows = [];

    this.selectedItem = { ...CashPreAccEditItemDefaultProps };
    this.positiveSelectedItem = { ...CashPreAccEditItemDefaultProps };
    this.negativeSelectedItem = { ...CashPreAccEditItemDefaultProps };
    this.selectedDimension = { ...CashPreAccEditDimensionDefaultProps };
    this.positiveSelectedDimension = { ...CashPreAccEditDimensionDefaultProps };
    this.negativeSelectedDimension = { ...CashPreAccEditDimensionDefaultProps };

    this.resetDeltas();
  }

  async loadEditorData(statementId: number) {
    this.isItemsLoading = true;
    this.statementId = statementId;
    // A store singleton: ha a betöltés elhasal, a KORÁBBI kivonat adatai maradnának
    // a képernyőn tisztának hitt állapotban — ezért itt, a kérés előtt ürítünk.
    this.resetEditorState();
    try {
      const data: CashPreAccEditorData = await CashPreAccountingApi.getEditorData(statementId);
      this.head = data.head;
      this.originalAccStatus = data.originalAccStatus;
      this.editable = data.editable;
      this.reAccountable = data.reAccountable;
      this.uploadable = data.uploadable;
      this.docItems = data.docItems ?? [];

      // A DTO-kon nincsenek UI-flagek (selected/inEdit) — a defaultokkal egészítjük ki
      this.items = (data.items ?? []).map((x) => ({
        ...CashPreAccEditItemDefaultProps,
        ...x,
      }));
      this.originalItems = JSON.parse(JSON.stringify(this.items));
      this.dimensionsRows = (data.dimensions ?? []).map((x) => ({
        ...CashPreAccEditDimensionDefaultProps,
        ...x,
      }));
      this.originalDimensionsRows = JSON.parse(JSON.stringify(this.dimensionsRows));

      this.accNos = data.accNos;
      this.dimCodes = data.dimCodes;
      this.detailedTypes = data.detailedTypes;
      this.flags = data.flags;

      this.resetDeltas();
      this.selectedItem = { ...CashPreAccEditItemDefaultProps };
      this.positiveSelectedItem = { ...CashPreAccEditItemDefaultProps };
      this.negativeSelectedItem = { ...CashPreAccEditItemDefaultProps };
      this.selectedDimension = { ...CashPreAccEditDimensionDefaultProps };
    } finally {
      this.isItemsLoading = false;
    }
  }

  // A kísérő-lookupok (VII-X. dim fülek) — elég egyszer betölteni a szerkesztő életére
  async loadDimLookups() {
    if (this.dimLookupsLoaded) return;
    const [cars, projects, pos, users, stations, tools] = await Promise.all([
      CashPreAccountingApi.getEditorCars(),
      CashPreAccountingApi.getEditorProjects(),
      CashPreAccountingApi.getEditorPos(),
      CashPreAccountingApi.getEditorUsers(),
      CashPreAccountingApi.getEditorStations(),
      CashPreAccountingApi.getEditorTools(),
    ]);
    this.serCars = cars.map((x) => ({ ...x, idString: String(x.id) }));
    this.projects = projects.map((x) => ({ ...x, idString: String(x.id) }));
    this.pos = pos.map((x) => ({ ...x, idString: String(x.id) }));
    this.users = users;
    this.stations = stations;
    this.tools = tools;
    this.dimLookupsLoaded = true;
  }

  buildSavePayload(): CashPreAccSaveRequest {
    return {
      originalAccStatus: this.originalAccStatus,
      items: this.items.map((x) => ({
        id: x.id,
        accCode: x.accCode,
        detailedType: x.detailedType,
        dimCode1: x.dimCode1,
        dimCode2: x.dimCode2,
        dimCode3: x.dimCode3,
        note: x.note,
      })),
      dimensions: this.dimensionsRows.map((d) => ({
        id: d.id,
        head: d.head,
        dimCodeType: d.dimCodeType,
        dimCode: d.dimCode,
        total: Number(d.total) || 0,
        project: d.project,
        poId: d.poId,
        userId: d.userId,
        stationId: d.stationId,
        toolId: d.toolId,
        carId: d.carId,
        extProdParts: d.extProdParts,
        extProdPartner: d.extProdPartner,
        productParts: d.productParts,
        production: d.production,
        stock: d.stock,
      })),
      deletedDimensionIds: [...this.deletedDimensions],
    };
  }

  async save(): Promise<CashPreAccSaveResult> {
    this.isSaving = true;
    try {
      return await CashPreAccountingApi.save(this.statementId, this.buildSavePayload());
    } finally {
      this.isSaving = false;
    }
  }

  // K2D pb_recontir: a kivonat újrakontírozása. SAJÁT végpont, nem a böngésző
  // csoportosa 1 elemmel — a K2D-ben itt a 3-as "Feladható" státusz is mehet.
  async reAccount(): Promise<CashPreAccReAccountResult> {
    this.isReAccounting = true;
    try {
      return await CashPreAccountingApi.reAccountSingle(this.statementId);
    } finally {
      this.isReAccounting = false;
    }
  }

  async isValidPartCode(partCode: string) {
    return await CashPreAccountingApi.validatePart(partCode);
  }

  async ValidPartnerName(partnerName: string) {
    return await CashPreAccountingApi.validatePartner(partnerName);
  }
}

const store = new CashPreAccEditorStore();
export default store;
